import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_BASE_URL, apiGet, apiPost, fetchAllPaginated } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import ConfirmarTodosPresentesModal from '../components/ConfirmarTodosPresentesModal';
import { getFeriadoNacional } from '../utils/feriados';
import { hojeLocal } from '../utils/datas';
import { chaveDaAulaDoDia, mesmaAula } from '../utils/aulasDoDia';
import { alunoEstaNaTurma } from '../utils/statusAluno';

/**
 * Frequência da turma: entra-se pela data, escolhe-se a aula e lança-se a
 * chamada dela.
 *
 * É a aba "Frequência" do Diário da Turma. Já foram duas abas — "Frequência
 * por Aula", que só abria aulas já registradas no Registro de Aulas, e
 * "Chamada do Dia", esta — gravando a mesma chamada; a duplicidade fazia o
 * professor se perguntar em qual lançar. Ficou esta, que cobre os dois
 * casos: a aula pode já estar no diário ou não (o backend a cria a partir do
 * slot da grade). Com `embedded`, o cabeçalho próprio some para não duplicar
 * o do Diário.
 *
 * `aulaInicial` ({ data, periodo, idAula }) abre a tela já numa aula: é como
 * a Grade Horária e o Registro de Aulas mandam o professor direto para a
 * chamada certa. Vale só na abertura; depois quem manda é a navegação.
 *
 * `idSubject` é a disciplina escolhida no topo do Diário. Sem ela, o
 * calendário pintava de verde os dias em que qualquer disciplina lançou
 * chamada, enquanto as abas vizinhas — recortadas pela disciplina — diziam
 * que o mês estava vazio. Na rota solta não há disciplina e o calendário
 * segue mostrando a turma inteira, que ali é o que se quer.
 */
function Frequencia({ embedded = false, idSubject = null, aulaInicial = null }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useTheme();

  // Estados principais
  const [turma, setTurma] = useState(null);
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data de hoje (AAAA-MM-DD) — usada para impedir o registro de
  // frequência em datas futuras (no calendário, no input e ao salvar).
  const todayStr = hojeLocal();
  // A data de abertura vem da aula indicada, quando há uma — nunca futura.
  const dataInicial = aulaInicial?.data && aulaInicial.data <= todayStr
    ? aulaInicial.data
    : todayStr;

  // Estados de frequência
  const [attendanceDate, setAttendanceDate] = useState(dataInicial);
  const [attendanceData, setAttendanceData] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceError, setAttendanceError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Calendário de frequência
  const [calendarMonth, setCalendarMonth] = useState(Number(dataInicial.slice(5, 7)) - 1);
  const [calendarYear, setCalendarYear] = useState(Number(dataInicial.slice(0, 4)));
  const [attendanceDates, setAttendanceDates] = useState([]);
  const [totalDiasRegistrados, setTotalDiasRegistrados] = useState(0);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [clearingDay, setClearingDay] = useState(false);
  const [clearingLesson, setClearingLesson] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // A data selecionada foi consolidada a partir das aulas do Diário
  // (source='diario' no backend). Nesse caso o Diário é a fonte da verdade e
  // o professor precisa saber que editar aqui vale só até a próxima chamada
  // por aula.
  const [diaVindoDoDiario, setDiaVindoDoDiario] = useState(false);
  // Mesma proteção da chamada por aula: o formulário nasce com todos
  // presentes, então salvar um dia sem nenhuma falta pede confirmação.
  const [confirmandoTodosPresentes, setConfirmandoTodosPresentes] = useState(false);
  // Justificativa digitada uma vez e aplicada a todos os que faltaram (dia
  // de eleição, evento da escola) — antes era aluno por aluno.
  const [justificativaEmBloco, setJustificativaEmBloco] = useState('');

  // Opções de status
  const attendanceStatusOptions = [
    { value: 'presente', label: 'Presente', color: 'green' },
    { value: 'ausente', label: 'Ausente', color: 'red' },
    { value: 'justificado', label: 'Justificado', color: 'yellow' },
    { value: 'atrasado', label: 'Atrasado', color: 'blue' },
  ];

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  // Permissões: somente Administrador e Professor podem registrar/alterar
  // a frequência da turma. Demais perfis (Coordenador, Supervisor, Gestor,
  // Referência Técnica) têm apenas leitura.
  const canEdit = [ROLES.ADMIN, ROLES.PROFESSOR].includes(user?.role);

  // Excluir a frequência do dia inteiro é uma correção de erro de
  // lançamento — fica com Administrador e Coordenador.
  const canClearDay = [ROLES.ADMIN, ROLES.COORDENADOR].includes(user?.role);

  // Feriado nacional: não há aula, então o dia não aceita lançamento.
  const feriadoSelecionado = getFeriadoNacional(attendanceDate);

  // Aulas previstas na grade horária para a data escolhida. A chamada é de
  // uma AULA (disciplina + horário), nunca do dia inteiro: o backend recusa
  // um lançamento sem disciplina. Aqui a pessoa escolhe qual aula está
  // lançando, e o backend cria a aula no diário a partir do slot da grade
  // se ela ainda não existir — este é o caminho curto, para quem quer só
  // fazer a chamada sem passar antes pelo Registro de Aulas.
  const [dayLessons, setDayLessons] = useState([]);
  // Nasce carregando: até as aulas do dia chegarem não se sabe de qual aula
  // é a chamada a mostrar.
  const [loadingDayLessons, setLoadingDayLessons] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState(null);
  // Aulas geminadas que recebem esta mesma chamada (chaves de
  // `chaveDaAulaDoDia`). A chamada é feita uma vez e vale para todas. Quem
  // acabou de registrar aulas geminadas chega com elas já marcadas — direto
  // do pedido, sem esperar a lista do dia: a lista é relida várias vezes, e
  // marcar ali era desfeito pela releitura seguinte.
  const [replicarEm, setReplicarEm] = useState(
    () => (aulaInicial?.geminadas || []).map((idAula) => `aula-${idAula}`)
  );
  // A aula pedida na abertura (`aulaInicial`), consumida na primeira busca
  // das aulas daquela data.
  const aulaPedida = useRef(aulaInicial);
  const [hasSchedule, setHasSchedule] = useState(true);
  // Feriado municipal, recesso, formação: veio do Calendário Letivo. É
  // diferente do feriado nacional, que a tabela local já conhece.
  const [nonSchoolDay, setNonSchoolDay] = useState(null);

  // Buscar dados da turma e alunos
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [turmaData, alunosData] = await Promise.all([
        apiGet(`/classes/${id}/`),
        // id_class no servidor: a tela baixava TODOS os alunos da rede para
        // ficar com os 30 desta turma.
        fetchAllPaginated('/students/', { id_class: id }),
      ]);

      setTurma(turmaData);

      // Quem foi transferido ou desistiu sai da chamada. A regra é a mesma do
      // backend (api/alunos_turma.py).
      const turmaAlunos = alunosData.filter(alunoEstaNaTurma);
      setAlunos(turmaAlunos);

      // Inicializar dados de frequência
      const initialData = {};
      turmaAlunos.forEach(aluno => {
        initialData[aluno.id_student || aluno.id] = {
          status: 'presente',
          justification: ''
        };
      });
      setAttendanceData(initialData);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados da turma. Verifique se a turma existe.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Buscar datas com frequência registrada.
  // Usa /calendar_dates/, que devolve as DATAS distintas do mês — a listagem
  // paginada de registros só trazia os primeiros 100 (um por aluno/dia), o
  // que deixava a maior parte dos dias sem pintar de verde no calendário.
  const fetchAttendanceDates = useCallback(async (classId, month, year) => {
    setLoadingCalendar(true);
    try {
      const params = {
        id_class: classId,
        month: `${year}-${String(month + 1).padStart(2, '0')}`,
      };
      if (idSubject) params.id_subject = idSubject;
      const response = await apiGet('/student-attendance/calendar_dates/', params);
      setAttendanceDates(response.dates || []);
      setTotalDiasRegistrados(response.total_days || 0);
    } catch (err) {
      console.error('Erro ao buscar datas de frequência:', err);
      setAttendanceDates([]);
    } finally {
      setLoadingCalendar(false);
    }
  }, [idSubject]);

  // O calendário recarrega quando muda o mês exibido — ou a disciplina, que
  // muda o recorte do que conta como "dia com frequência".
  useEffect(() => {
    fetchAttendanceDates(id, calendarMonth, calendarYear);
  }, [id, calendarMonth, calendarYear, fetchAttendanceDates]);

  // Aulas da grade para a data escolhida. `for_date` já devolve `can_edit`
  // por aula, aplicando a mesma regra do salvamento — o professor só lança
  // as aulas dele.
  //
  // `manter` é a aula que estava escolhida: depois de salvar ou limpar, a
  // lista é relida (a aula pode ter acabado de nascer no diário) e a escolha
  // fica onde estava — antes voltava para a primeira aula do dia, e quem
  // acabava de lançar a 3ª aula via a chamada da 1ª. `silencioso` relê sem
  // o aviso de "carregando", que recarregaria a chamada à toa.
  const fetchDayLessons = useCallback(async (classId, date, { manter = null, silencioso = false } = {}) => {
    if (!classId || !date) return;
    if (!silencioso) setLoadingDayLessons(true);
    try {
      const resposta = await apiGet('/class-schedule/for_date/', {
        id_class: classId,
        date,
      });
      const lista = Array.isArray(resposta?.lessons) ? resposta.lessons : [];
      setDayLessons(lista);
      setHasSchedule(resposta?.has_schedule !== false);
      setNonSchoolDay(resposta?.non_school_day || null);

      const minhas = lista.filter((l) => l.can_edit !== false);
      let escolhida = null;
      const pedida = aulaPedida.current;
      if (pedida && pedida.data === date) {
        aulaPedida.current = null;
        escolhida = minhas.find((l) => (
          pedida.idAula
            ? l.id_diary_entry === pedida.idAula
            : pedida.periodo && String(l.period) === String(pedida.periodo)
        )) || null;
      } else if (manter) {
        escolhida = minhas.find((l) => mesmaAula(l, manter)) || null;
      }
      // Sem pedido, pré-seleciona a primeira aula que a pessoa pode lançar,
      // para o caso comum (uma aula só, ou o professor com uma aula no dia).
      setSelectedLesson(escolhida || (minhas.length ? minhas[0] : null));
    } catch (err) {
      console.error('Erro ao buscar as aulas do dia:', err);
      setDayLessons([]);
      setSelectedLesson(null);
      // Numa API antiga ou fora do ar, não trave a tela dizendo que a turma
      // não tem grade — isso seria mentira e mandaria a pessoa para o lugar
      // errado. O erro real aparece ao salvar.
      setHasSchedule(true);
      setNonSchoolDay(null);
    } finally {
      setLoadingDayLessons(false);
    }
  }, []);

  useEffect(() => {
    fetchDayLessons(id, attendanceDate);
  }, [id, attendanceDate, fetchDayLessons]);

  // Chamada a mostrar no formulário. É a da AULA escolhida, lida da própria
  // aula (/diary-attendance/by_entry/). Antes vinha do resumo do dia: num dia
  // com duas aulas, escolher a 2ª mostrava as faltas da 1ª — e salvar
  // copiava essas faltas para a 2ª. Aula ainda sem registro no diário não
  // tem chamada: o formulário nasce com todos presentes.
  //
  // Sem aula escolhida (dia só com aulas dos colegas, ou perfil que não
  // lança) sobra o resumo do dia, que é o que dá para mostrar.
  const pedidoDaChamada = useRef(0);
  const carregarChamada = useCallback(async (aula, date) => {
    const pedido = ++pedidoDaChamada.current;
    const montar = (registros) => {
      const dados = {};
      alunos.forEach(aluno => {
        const studentId = aluno.id_student || aluno.id;
        const existente = registros.find(r => r.id_student === studentId);
        dados[studentId] = {
          status: existente ? existente.status : 'presente',
          justification: existente ? existente.justification || '' : '',
        };
      });
      return dados;
    };

    setLoadingAttendance(true);
    try {
      let registros = [];
      let doDiario = false;
      if (aula?.id_diary_entry) {
        const resposta = await apiGet('/diary-attendance/by_entry/', {
          id_diary_entry: aula.id_diary_entry,
        });
        registros = Array.isArray(resposta) ? resposta : (resposta.results || []);
      } else if (!aula) {
        const resposta = await apiGet('/student-attendance/', {
          id_class: id,
          attendance_date: date,
        });
        registros = resposta.results || resposta || [];
        doDiario = registros.some(r => r.source === 'diario');
      }
      // Trocou de aula ou de data enquanto esta resposta vinha: ela já não
      // vale, e sobrescreveria a chamada certa.
      if (pedido !== pedidoDaChamada.current) return;
      setDiaVindoDoDiario(doDiario);
      setAttendanceData(montar(registros));
    } catch (err) {
      console.error('Erro ao carregar frequência:', err);
      if (pedido === pedidoDaChamada.current) setAttendanceData(montar([]));
    } finally {
      if (pedido === pedidoDaChamada.current) setLoadingAttendance(false);
    }
  }, [id, alunos]);

  // A aula escolhida se identifica pelo registro no diário, quando já tem
  // um; senão pelo horário. O objeto em si é recriado a cada releitura das
  // aulas do dia e não serve de gatilho.
  const chaveDaAula = selectedLesson ? chaveDaAulaDoDia(selectedLesson) : '';

  // As outras aulas do dia em que esta mesma chamada pode valer: da mesma
  // disciplina, que a pessoa pode lançar e que aconteceram.
  const aulasGeminadas = selectedLesson
    ? dayLessons.filter((l) => l.can_edit !== false
      && !l.not_held
      && !l.substituted
      && String(l.id_subject) === String(selectedLesson.id_subject)
      && (l.id_diary_entry || l.id_schedule)
      && !mesmaAula(l, selectedLesson))
    : [];
  const geminadasMarcadas = aulasGeminadas.filter((l) => replicarEm.includes(chaveDaAulaDoDia(l)));
  const aulaEscolhida = useRef(selectedLesson);
  aulaEscolhida.current = selectedLesson;

  useEffect(() => {
    if (loading || loadingDayLessons) return;
    carregarChamada(aulaEscolhida.current, attendanceDate);
  }, [loading, loadingDayLessons, chaveDaAula, attendanceDate, carregarChamada]);

  // Funções do calendário
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDateForComparison = (day) => {
    const month = String(calendarMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${calendarYear}-${month}-${dayStr}`;
  };

  const isDateWithAttendance = (day) => {
    const dateStr = formatDateForComparison(day);
    return attendanceDates.includes(dateStr);
  };

  // Troca a data. A aula escolhida cai junto, e as aulas do dia passam a
  // "carregando" já aqui: sem isso a chamada da aula da data anterior seria
  // buscada para a data nova antes de a lista nova chegar.
  const trocarData = (novaData) => {
    setAttendanceError(null);
    setSaveSuccess(false);
    if (novaData === attendanceDate) return;
    setSelectedLesson(null);
    // As aulas geminadas marcadas eram as daquele dia.
    setReplicarEm([]);
    setLoadingDayLessons(true);
    setAttendanceDate(novaData);
  };

  const handleCalendarDateClick = (day) => {
    const dateStr = formatDateForComparison(day);
    // Bloqueia datas futuras.
    if (dateStr > todayStr) {
      setAttendanceError('Não é permitido registrar frequência em data futura.');
      return;
    }
    trocarData(dateStr);
  };

  // Atualizar quando a data muda via input
  const handleAttendanceDateChange = (newDate) => {
    // Bloqueia datas futuras digitadas/selecionadas no input.
    if (newDate > todayStr) {
      setAttendanceError('Não é permitido registrar frequência em data futura.');
      return;
    }
    // Input de data apagado: nada a carregar.
    if (!newDate) return;
    trocarData(newDate);

    // Atualizar calendário para mostrar o mês da data selecionada
    const dateObj = new Date(newDate + 'T00:00:00');
    setCalendarMonth(dateObj.getMonth());
    setCalendarYear(dateObj.getFullYear());
  };

  // Atualizar status de um aluno
  const handleAttendanceChange = (studentId, status) => {
    setSaveSuccess(false);
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        status
      }
    }));
  };

  // Atualizar justificativa de um aluno
  const handleJustificationChange = (studentId, justification) => {
    setSaveSuccess(false);
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        justification
      }
    }));
  };

  // A mesma justificativa para todos os que estão como ausente ou
  // justificado — quem estava presente não recebe motivo de falta.
  const handleJustificarTodos = () => {
    const texto = justificativaEmBloco.trim();
    if (!texto) return;
    setSaveSuccess(false);
    setAttendanceData(prev => {
      const novo = { ...prev };
      Object.entries(prev).forEach(([studentId, dados]) => {
        if (dados.status === 'ausente' || dados.status === 'justificado') {
          novo[studentId] = { ...dados, justification: texto };
        }
      });
      return novo;
    });
  };

  // Marcar todos com um status
  const handleMarkAll = (status) => {
    setSaveSuccess(false);
    const updatedData = {};
    alunos.forEach(aluno => {
      const studentId = aluno.id_student || aluno.id;
      updatedData[studentId] = {
        status,
        justification: attendanceData[studentId]?.justification || ''
      };
    });
    setAttendanceData(updatedData);
  };

  const ninguemFaltou = () => {
    const registros = Object.values(attendanceData);
    return registros.length > 0 && registros.every(d => d.status === 'presente');
  };

  // Pedido de salvar vindo do botão: dia sem nenhuma falta abre a
  // confirmação em vez de gravar direto.
  const handleSaveAttendanceClick = () => {
    if (!feriadoSelecionado && ninguemFaltou()) {
      setConfirmandoTodosPresentes(true);
      return;
    }
    handleSaveAttendance(false);
  };

  // Salvar frequência. `confirmadoTodosPresentes` só chega como true pelo
  // botão do modal — é assim que o backend distingue uma chamada conferida
  // de um formulário salvo no padrão.
  const handleSaveAttendance = async (confirmadoTodosPresentes = false) => {
    setConfirmandoTodosPresentes(false);
    if (!turma || !attendanceDate) return;

    if (feriadoSelecionado) {
      setAttendanceError(`${attendanceDate.split('-').reverse().join('/')} é feriado nacional (${feriadoSelecionado}). Não há frequência a lançar.`);
      return;
    }

    // Trava final: nunca envia frequência de data futura ao backend.
    if (attendanceDate > todayStr) {
      setAttendanceError('Não é permitido registrar frequência em data futura.');
      return;
    }

    // A chamada é de uma aula. Com aulas no dia, a pessoa escolhe qual.
    // Sem nenhuma — turma ainda sem grade — a chamada não fica travada: vai
    // com a disciplina escolhida no Diário e o backend cria a aula avulsa.
    // Só falta mesmo quando não há disciplina de onde partir.
    const disciplinaDaChamada = selectedLesson ? selectedLesson.id_subject : idSubject;
    if (!disciplinaDaChamada) {
      setAttendanceError(
        dayLessons.length > 0
          ? 'Escolha a aula que está sendo lançada. A frequência é registrada por aula (disciplina e horário).'
          : 'Escolha a disciplina no topo do Diário da Turma para lançar a chamada.'
      );
      return;
    }
    // "Escolha a aula" só faz sentido se houver alguma que ele possa lançar:
    // com o dia cheio de aulas dos colegas, cobrar uma escolha travaria a
    // chamada dele em vez de orientá-la.
    const temAulaDele = dayLessons.some((l) => l.can_edit !== false);
    if (!selectedLesson && temAulaDele) {
      setAttendanceError('Escolha a aula que está sendo lançada. A frequência é registrada por aula (disciplina e horário).');
      return;
    }

    setSavingAttendance(true);
    setAttendanceError(null);
    setSaveSuccess(false);

    try {
      const attendances = Object.entries(attendanceData).map(([studentId, data]) => ({
        id_student: parseInt(studentId),
        status: data.status,
        justification: data.justification || null
      }));

      await apiPost('/student-attendance/bulk_register/', {
        id_class: parseInt(id),
        attendance_date: attendanceDate,
        // Identificam a aula. Com a aula já registrada no diário, mandamos
        // o id dela; senão, disciplina (e período, quando há grade), e o
        // backend cria a aula — do slot, se existir, ou avulsa.
        id_diary_entry: selectedLesson?.id_diary_entry || undefined,
        id_subject: disciplinaDaChamada,
        class_period: selectedLesson?.period || undefined,
        // Aulas geminadas: a mesma chamada, gravada junto, nas outras aulas
        // marcadas — as já registradas pelo id, as da grade pelo período.
        replicar_em: geminadasMarcadas.filter((l) => l.id_diary_entry)
          .map((l) => l.id_diary_entry),
        replicar_em_periodos: geminadasMarcadas.filter((l) => !l.id_diary_entry)
          .map((l) => l.period),
        all_present_confirmed: confirmadoTodosPresentes || undefined,
        attendances
      });

      // A aula pode ter acabado de ser criada: relê o dia para que o
      // seletor passe a mostrá-la como lançada, sem tirar a escolha dela.
      await fetchDayLessons(id, attendanceDate, { manter: selectedLesson, silencioso: true });

      // Atualizar a lista de datas com frequência
      await fetchAttendanceDates(id, calendarMonth, calendarYear);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Erro ao salvar frequência:', err);
      setAttendanceError(err.message || 'Erro ao salvar frequência. Tente novamente.');
    } finally {
      setSavingAttendance(false);
    }
  };

  // A aula escolhida já tem chamada lançada no diário: é o que habilita o
  // PDF da chamada e a exclusão dela.
  const aulaComChamada = !!(selectedLesson?.id_diary_entry && selectedLesson?.attendance_filled);

  // Limpar a chamada de uma aula: Administrador e Coordenador em qualquer
  // aula; o professor nas aulas dele (`can_edit`), a mesma regra do backend
  // (/diary-attendance/clear_entry/). Sem isso, o único caminho de correção
  // dele era apagar a aula inteira no Registro de Aulas.
  const canClearLesson = canClearDay
    || (user?.role === ROLES.PROFESSOR && selectedLesson?.can_edit !== false);

  // Apaga a chamada da aula escolhida (Administrador e Coordenador). O
  // registro de aula em si continua — só a frequência é zerada, para ser
  // refeita; o resumo do dia é reconsolidado no backend.
  const handleClearLesson = async () => {
    if (!aulaComChamada) return;

    const dataBR = attendanceDate.split('-').reverse().join('/');
    const nomeDaAula = selectedLesson.period
      ? `${selectedLesson.period}ª aula`
      : 'aula avulsa';
    const confirmado = window.confirm(
      `Excluir a frequência lançada na ${nomeDaAula} de ${dataBR}?\n\n` +
      'A aula continua registrada; apenas a chamada é apagada, para ser refeita. ' +
      'Esta ação não pode ser desfeita.'
    );
    if (!confirmado) return;

    setClearingLesson(true);
    setAttendanceError(null);
    setSaveSuccess(false);

    try {
      await apiPost('/diary-attendance/clear_entry/', {
        id_diary_entry: selectedLesson.id_diary_entry,
      });

      const zerado = {};
      alunos.forEach(aluno => {
        zerado[aluno.id_student || aluno.id] = { status: 'presente', justification: '' };
      });
      setAttendanceData(zerado);
      await fetchDayLessons(id, attendanceDate, { manter: selectedLesson, silencioso: true });
      await fetchAttendanceDates(id, calendarMonth, calendarYear);
    } catch (err) {
      console.error('Erro ao excluir frequência da aula:', err);
      setAttendanceError(err.message || 'Erro ao excluir a frequência desta aula.');
    } finally {
      setClearingLesson(false);
    }
  };

  // PDF da chamada da aula escolhida, para impressão e assinatura.
  const handleDownloadPdf = async () => {
    if (!aulaComChamada) return;
    setDownloadingPdf(true);
    try {
      const qs = new URLSearchParams({ id_diary_entry: selectedLesson.id_diary_entry });
      const response = await fetch(`${API_BASE_URL}/diary-attendance/pdf/?${qs}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao gerar PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers.get('Content-Disposition');
      const fallback = `frequencia_${attendanceDate}.pdf`;
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '') || fallback
        : fallback;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
      alert(err.message || 'Não foi possível gerar o PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Excluir a frequência do dia inteiro (Administrador e Coordenador). Com a
  // chamada por aula, a correção normal é a da aula; esta sobra para o dia
  // que tem frequência sem nenhuma aula com chamada — lançamento antigo, do
  // tempo em que a chamada era do dia.
  const diaSemChamadaPorAula = attendanceDates.includes(attendanceDate)
    && !dayLessons.some(l => l.attendance_filled);

  const handleClearDay = async () => {
    if (!turma || !attendanceDate) return;

    const dataBR = attendanceDate.split('-').reverse().join('/');
    const confirmado = window.confirm(
      `Excluir a frequência de ${dataBR}?\n\n` +
      `Todos os lançamentos dos ${alunos.length} alunos desta turma nesse dia serão apagados. ` +
      'Esta ação não pode ser desfeita.'
    );
    if (!confirmado) return;

    setClearingDay(true);
    setAttendanceError(null);
    setSaveSuccess(false);

    try {
      await apiPost('/student-attendance/clear_day/', {
        id_class: parseInt(id),
        attendance_date: attendanceDate,
      });

      // Volta o formulário ao estado padrão e recarrega o calendário.
      const initialData = {};
      alunos.forEach(aluno => {
        initialData[aluno.id_student || aluno.id] = { status: 'presente', justification: '' };
      });
      setAttendanceData(initialData);
      await fetchAttendanceDates(id, calendarMonth, calendarYear);
    } catch (err) {
      console.error('Erro ao excluir frequência do dia:', err);
      setAttendanceError(err.message || 'Erro ao excluir a frequência do dia. Tente novamente.');
    } finally {
      setClearingDay(false);
    }
  };

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Frequência..."
        subtitle="Buscando alunos e registros da turma..."
        size="xl"
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`p-6 rounded-xl text-center max-w-md ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className={`mb-4 ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/turmas')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode
                ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              Voltar para Turmas
            </button>
            <button
              onClick={fetchData}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode
                ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
                : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20'
              }`}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header — só na rota própria. Embutida no Diário, o cabeçalho é o de lá. */}
      {!embedded && (
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/turmas')}
              className={`p-2 rounded-lg transition-colors ${darkMode
                ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
              }`}
              title="Voltar para Turmas"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
              ? 'bg-gradient-to-r from-gray-50 to-gray-400'
              : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
              Frequência
            </h1>
          </div>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {turma?.class_name} - {turma?.school_name}
          </p>
        </div>

        {saveSuccess && (
          <div className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode
            ? 'bg-[#13983D]/20 text-[#13983D] border border-[#13983D]/30'
            : 'bg-[#13983D]/10 text-[#13983D] border border-[#13983D]/20'
          }`}>
            Frequência salva com sucesso!
          </div>
        )}
      </header>
      )}

      {embedded && saveSuccess && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
          ? 'bg-[#13983D]/20 text-[#13983D] border border-[#13983D]/30'
          : 'bg-[#13983D]/10 text-[#13983D] border border-[#13983D]/20'
        }`}>
          Frequência salva com sucesso!
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna do Calendário */}
        <div className={`rounded-xl p-5 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
          <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Calendário de Frequência
          </h3>
          <p className={`text-xs mb-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Clique em uma data para registrar ou editar a frequência.
          </p>

          {/* Navegação do mês */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => {
                if (calendarMonth === 0) {
                  setCalendarMonth(11);
                  setCalendarYear(calendarYear - 1);
                } else {
                  setCalendarMonth(calendarMonth - 1);
                }
              }}
              className={`p-2 rounded-lg transition-colors ${darkMode
                ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className={`font-semibold text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {monthNames[calendarMonth]} {calendarYear}
            </span>
            <button
              onClick={() => {
                if (calendarMonth === 11) {
                  setCalendarMonth(0);
                  setCalendarYear(calendarYear + 1);
                } else {
                  setCalendarMonth(calendarMonth + 1);
                }
              }}
              className={`p-2 rounded-lg transition-colors ${darkMode
                ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Dias da semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(day => (
              <div
                key={day}
                className={`text-center text-xs font-semibold py-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Dias do mês */}
          {loadingCalendar ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Espaços vazios para o início do mês */}
              {Array.from({ length: getFirstDayOfMonth(calendarMonth, calendarYear) }).map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square" />
              ))}

              {/* Dias do mês */}
              {Array.from({ length: getDaysInMonth(calendarMonth, calendarYear) }).map((_, index) => {
                const day = index + 1;
                const dateStr = formatDateForComparison(day);
                const hasAttendance = isDateWithAttendance(day);
                const feriado = getFeriadoNacional(dateStr);
                const isToday =
                  day === new Date().getDate() &&
                  calendarMonth === new Date().getMonth() &&
                  calendarYear === new Date().getFullYear();
                const isSelected = dateStr === attendanceDate;
                const isFuture = dateStr > todayStr;
                // O dia não aceita lançamento por um de dois motivos: é
                // feriado (não houve aula) ou ainda não aconteceu.
                const bloqueado = !!feriado || isFuture;

                return (
                  <button
                    key={day}
                    onClick={() => !bloqueado && handleCalendarDateClick(day)}
                    disabled={bloqueado}
                    className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                      bloqueado ? 'cursor-not-allowed' : 'hover:scale-105'
                    } ${
                      isFuture ? 'opacity-30' : ''
                    } ${
                      isSelected && !bloqueado
                        ? 'ring-2 ring-[#2E4974] ring-offset-2 ring-offset-slate-800'
                        : ''
                    } ${
                      feriado
                        ? darkMode
                          ? 'bg-slate-800/60 text-slate-600 line-through'
                          : 'bg-slate-50 text-slate-300 line-through'
                        : hasAttendance
                        ? 'bg-[#13983D] text-white shadow-lg shadow-[#13983D]/30 hover:bg-[#13983D]/80'
                        : isToday
                        ? darkMode
                          ? 'bg-[#0974BA]/30 text-[#0974BA] ring-2 ring-[#0974BA]'
                          : 'bg-[#0974BA]/10 text-[#0974BA] ring-2 ring-[#0974BA]'
                        : darkMode
                        ? 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    title={
                      feriado
                        ? `Feriado nacional — ${feriado}`
                        : isFuture
                        ? 'Não é possível registrar frequência em data futura'
                        : hasAttendance
                        ? 'Frequência registrada - clique para editar'
                        : 'Clique para registrar frequência'
                    }
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          )}

          {/* Legenda */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-4 pt-4 border-t border-slate-600/30">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#13983D]" />
              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Com frequência</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${darkMode ? 'bg-slate-600/50' : 'bg-slate-100'}`} />
              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sem frequência</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ring-2 ring-[#0974BA] ${darkMode ? 'bg-[#0974BA]/30' : 'bg-[#0974BA]/10'}`} />
              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Hoje</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${darkMode ? 'bg-slate-800/60' : 'bg-slate-50 border border-slate-200'}`} />
              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Feriado</span>
            </div>
          </div>

          {/* Estatísticas da turma */}
          <div className="mt-6 pt-4 border-t border-slate-600/30">
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Informações da Turma
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                  {alunos.length}
                </div>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Alunos</p>
              </div>
              <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                  {totalDiasRegistrados}
                </div>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Dias registrados</p>
              </div>
            </div>
          </div>
        </div>

        {/* Coluna do Registro de Frequência */}
        <div className={`lg:col-span-2 rounded-xl p-5 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
          <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Registro de Frequência
          </h3>

          {/* Data e Ações Rápidas */}
          <div className={`rounded-xl p-4 mb-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <label className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Data:
                </label>
                <input
                  type="date"
                  value={attendanceDate}
                  max={todayStr}
                  onChange={(e) => handleAttendanceDateChange(e.target.value)}
                  className={`px-4 py-2 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-600 border-slate-500 text-slate-200'
                    : 'bg-white border-slate-200 text-slate-700'
                  } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                />
              </div>
              {canEdit && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Marcar todos:</span>
                  {attendanceStatusOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleMarkAll(option.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        option.color === 'green' ? 'bg-[#13983D]/20 text-[#13983D] hover:bg-[#13983D]/30' :
                        option.color === 'red' ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30' :
                        option.color === 'yellow' ? 'bg-[#F9B047]/20 text-[#F9B047] hover:bg-[#F9B047]/30' :
                        'bg-[#0974BA]/20 text-[#0974BA] hover:bg-[#0974BA]/30'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Justificativa em bloco: com "Marcar todos: Justificado", o
                motivo vai para a turma inteira de uma vez. */}
            {canEdit && Object.values(attendanceData).some(
              d => d.status === 'ausente' || d.status === 'justificado'
            ) && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <input
                  type="text"
                  value={justificativaEmBloco}
                  onChange={(e) => setJustificativaEmBloco(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleJustificarTodos(); }}
                  placeholder="Justificativa para todos os que faltaram (ex.: Eleições)"
                  className={`flex-1 min-w-[220px] px-3 py-1.5 rounded-lg text-xs ${darkMode
                    ? 'bg-slate-600 border-slate-500 text-slate-200 placeholder-slate-400'
                    : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
                  } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                />
                <button
                  type="button"
                  onClick={handleJustificarTodos}
                  disabled={!justificativaEmBloco.trim()}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    !justificativaEmBloco.trim()
                      ? darkMode
                        ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : darkMode
                        ? 'bg-[#F9B047]/20 text-[#F9B047] hover:bg-[#F9B047]/30'
                        : 'bg-[#F9B047]/20 text-[#B26B00] hover:bg-[#F9B047]/30'
                  }`}
                >
                  Aplicar a todos os que faltaram
                </button>
              </div>
            )}

            {feriadoSelecionado && (
              <p className={`text-xs mt-3 ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                Feriado nacional ({feriadoSelecionado}) — não há frequência a lançar nesta data.
              </p>
            )}

            {diaVindoDoDiario && !feriadoSelecionado && (
              <p className={`text-xs mt-3 ${darkMode ? 'text-[#0974BA]' : 'text-[#0974BA]'}`}>
                Esta data já tem chamada lançada por aula no Diário. O que aparece
                abaixo é a consolidação do dia — se a chamada de alguma aula for
                salva de novo, ela volta a valer sobre o que for alterado aqui.
              </p>
            )}
          </div>

          {/* Qual aula está sendo lançada. A frequência pertence a uma aula
              (disciplina + horário) — sem escolher, não há o que salvar. */}
          {!feriadoSelecionado && (
            <div className={`rounded-xl p-4 mb-4 ${darkMode
              ? 'bg-slate-800/50 border border-slate-700/10'
              : 'bg-white/80 border border-slate-200 shadow-sm'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className={`text-sm font-semibold uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Aula
                  </h3>
                  <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    A chamada é da aula, não do dia inteiro. Escolha qual você está lançando.
                  </p>
                </div>
                {aulaComChamada && (
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      downloadingPdf
                        ? 'bg-slate-500 text-white cursor-not-allowed opacity-60'
                        : 'text-white bg-gradient-to-r from-[#E83A3C] to-[#C4292B] hover:shadow-lg hover:shadow-[#E83A3C]/25'
                    }`}
                    title="Baixar PDF da chamada desta aula"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {downloadingPdf ? 'Gerando...' : 'Baixar PDF'}
                  </button>
                )}
              </div>

              {loadingDayLessons ? (
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Carregando as aulas do dia...
                </p>
              ) : nonSchoolDay ? (
                <p className={`text-sm ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                  {nonSchoolDay.day_type_display}
                  {nonSchoolDay.description ? ` (${nonSchoolDay.description})` : ''}
                  {' — dia sem aula no calendário letivo, não há frequência a lançar.'}
                </p>
              ) : dayLessons.length === 0 ? (
                <p className={`text-sm ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                  {hasSchedule
                    ? 'Nenhuma aula prevista para esta data.'
                    : 'Esta turma ainda não tem grade horária para este dia. Pode lançar a chamada assim mesmo — ela entra como aula avulsa da disciplina escolhida no topo. Com a grade montada (Turmas › Grade Horária) a aula já vem com horário e período.'}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {dayLessons.map((aula) => {
                    const podeLancar = aula.can_edit !== false;
                    const escolhida = mesmaAula(selectedLesson, aula);
                    const nome = aula.actual_subject_name || aula.subject_name;
                    return (
                      <button
                        key={aula.id_diary_entry || `${aula.period}-${aula.id_subject}`}
                        type="button"
                        disabled={!podeLancar}
                        onClick={() => {
                          if (!mesmaAula(selectedLesson, aula)) setReplicarEm([]);
                          setSelectedLesson(aula);
                          setSaveSuccess(false);
                        }}
                        title={podeLancar ? undefined : 'Esta aula é de outro professor.'}
                        className={`px-3 py-2 rounded-lg text-xs font-medium text-left transition-all ${!podeLancar
                          ? darkMode
                            ? 'bg-slate-800/40 text-slate-600 cursor-not-allowed'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : escolhida
                            ? 'bg-gradient-to-r from-[#13983D] to-[#0974BA] text-white shadow-lg shadow-[#13983D]/25'
                            : darkMode
                              ? 'bg-slate-700/60 text-slate-200 hover:bg-slate-700'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                      >
                        <span className="block">
                          {aula.period ? `${aula.period}ª` : 'Aula avulsa'} — {nome}
                        </span>
                        {aula.time_label && (
                          <span className="block opacity-70 font-normal">{aula.time_label}</span>
                        )}
                        {aula.attendance_filled && (
                          <span className="block opacity-70 font-normal">chamada lançada</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Aulas geminadas: a chamada feita uma vez vale para as outras
                  aulas da mesma disciplina no dia. */}
              {canEdit && !loadingDayLessons && aulasGeminadas.length > 0 && (
                <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Aula geminada? Aplicar esta chamada também a:
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {aulasGeminadas.map((aula) => {
                      const chave = chaveDaAulaDoDia(aula);
                      const marcada = replicarEm.includes(chave);
                      return (
                        <label
                          key={chave}
                          className={`flex items-center gap-2 text-xs cursor-pointer ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                          <input
                            type="checkbox"
                            checked={marcada}
                            onChange={() => {
                              setSaveSuccess(false);
                              setReplicarEm((atual) => (marcada
                                ? atual.filter((c) => c !== chave)
                                : [...atual, chave]));
                            }}
                            className="w-4 h-4 accent-[#13983D]"
                          />
                          {aula.period ? `${aula.period}ª aula` : 'Aula avulsa'}
                          {aula.time_label ? ` • ${aula.time_label}` : ''}
                          {aula.attendance_filled ? ' (já tem chamada — será substituída)' : ''}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lista de Alunos */}
          {alunos.length === 0 ? (
            <div className={`rounded-xl p-8 text-center ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
              <div className="text-4xl mb-3">📚</div>
              <p className={`${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Nenhum aluno matriculado nesta turma.
              </p>
            </div>
          ) : loadingAttendance ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="w-8 h-8 border-4 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
                <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Carregando frequência...</p>
              </div>
            </div>
          ) : (
            <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
              <div className="max-h-[calc(100vh-450px)] min-h-[300px] overflow-y-auto overflow-x-auto">
                <table className="w-full text-sm min-w-[340px]">
                  <thead className="sticky top-0 z-10">
                    <tr className={darkMode ? 'bg-slate-700' : 'bg-slate-100'}>
                      <th className={`text-left px-2 md:px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Aluno</th>
                      <th className={`text-center px-2 md:px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Status</th>
                      <th className={`text-left px-2 md:px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Justificativa</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-slate-600' : 'divide-slate-200'}`}>
                    {alunos.map(aluno => {
                      const studentId = aluno.id_student || aluno.id;
                      const studentData = attendanceData[studentId] || { status: 'presente', justification: '' };
                      return (
                        <tr key={studentId} className={`transition-colors ${darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-100'}`}>
                          <td className={`px-2 md:px-4 py-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                            <div>
                              <p className="font-medium">{aluno.student_name}</p>
                              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {aluno.student_serial || 'Sem matrícula'}
                              </p>
                            </div>
                          </td>
                          <td className="px-2 md:px-4 py-3">
                            <div className="flex justify-center gap-1">
                              {attendanceStatusOptions.map(option => (
                                <button
                                  key={option.value}
                                  onClick={() => canEdit && handleAttendanceChange(studentId, option.value)}
                                  disabled={!canEdit}
                                  className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                                    !canEdit ? 'cursor-not-allowed opacity-50' : ''
                                  } ${
                                    studentData.status === option.value
                                      ? option.color === 'green' ? 'bg-[#13983D] text-white shadow-lg shadow-[#13983D]/30' :
                                        option.color === 'red' ? 'bg-[#E83A3C] text-white shadow-lg shadow-[#E83A3C]/30' :
                                        option.color === 'yellow' ? 'bg-[#F9B047] text-white shadow-lg shadow-[#F9B047]/30' :
                                        'bg-[#0974BA] text-white shadow-lg shadow-[#0974BA]/30'
                                      : option.color === 'green' ? 'bg-[#13983D]/20 text-[#13983D] hover:bg-[#13983D]/30' :
                                        option.color === 'red' ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30' :
                                        option.color === 'yellow' ? 'bg-[#F9B047]/20 text-[#F9B047] hover:bg-[#F9B047]/30' :
                                        'bg-[#0974BA]/20 text-[#0974BA] hover:bg-[#0974BA]/30'
                                  }`}
                                  title={option.label}
                                >
                                  {option.label.charAt(0)}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td className="px-2 md:px-4 py-3">
                            {(studentData.status === 'ausente' || studentData.status === 'justificado') && (
                              <input
                                type="text"
                                value={studentData.justification}
                                onChange={(e) => canEdit && handleJustificationChange(studentId, e.target.value)}
                                disabled={!canEdit}
                                placeholder="Motivo (opcional)"
                                className={`w-full px-3 py-1.5 rounded-lg text-xs ${darkMode
                                  ? 'bg-slate-600 border-slate-500 text-slate-200 placeholder-slate-400'
                                  : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
                                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${!canEdit ? 'cursor-not-allowed opacity-50' : ''}`}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Resumo */}
          {alunos.length > 0 && (
            <div className={`rounded-xl p-4 mt-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
              <div className="flex flex-wrap justify-center gap-6">
                {attendanceStatusOptions.map(option => {
                  const count = Object.values(attendanceData).filter(d => d.status === option.value).length;
                  return (
                    <div key={option.value} className="text-center">
                      <div className={`text-2xl font-bold ${
                        option.color === 'green' ? 'text-[#13983D]' :
                        option.color === 'red' ? 'text-[#E83A3C]' :
                        option.color === 'yellow' ? 'text-[#F9B047]' :
                        'text-[#0974BA]'
                      }`}>
                        {count}
                      </div>
                      <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{option.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {attendanceError && (
            <div className={`p-3 rounded-lg mt-4 ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
              <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{attendanceError}</p>
            </div>
          )}

          {/* Botões de ação */}
          {(canEdit || canClearDay) && alunos.length > 0 && (
            <div className="flex flex-wrap justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => navigate('/turmas')}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                }`}
                disabled={savingAttendance || clearingDay || clearingLesson}
              >
                Voltar
              </button>

              {/* Correção de chamada errada: zera a frequência da aula. */}
              {canClearLesson && aulaComChamada && (
                <button
                  onClick={handleClearLesson}
                  disabled={clearingLesson || savingAttendance || loadingAttendance}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    clearingLesson || savingAttendance || loadingAttendance
                      ? 'bg-slate-500 text-white cursor-not-allowed'
                      : darkMode
                      ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30 border border-[#E83A3C]/30'
                      : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20 border border-[#E83A3C]/20'
                  }`}
                  title="Apaga a chamada desta aula; o registro de aula continua"
                >
                  {clearingLesson ? 'Excluindo...' : 'Excluir frequência da aula'}
                </button>
              )}

              {/* Frequência do dia sem chamada por aula (lançamento antigo):
                  só dá para apagar o dia inteiro. */}
              {canClearDay && diaSemChamadaPorAula && (
                <button
                  onClick={handleClearDay}
                  disabled={clearingDay || savingAttendance || loadingAttendance}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    clearingDay || savingAttendance || loadingAttendance
                      ? 'bg-slate-500 text-white cursor-not-allowed'
                      : darkMode
                      ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30 border border-[#E83A3C]/30'
                      : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20 border border-[#E83A3C]/20'
                  }`}
                  title="Apaga a frequência lançada nesta data para toda a turma"
                >
                  {clearingDay ? 'Excluindo...' : 'Excluir frequência do dia'}
                </button>
              )}

              {canEdit && (
                <button
                  onClick={handleSaveAttendanceClick}
                  disabled={savingAttendance || loadingAttendance || clearingDay || clearingLesson || !!feriadoSelecionado}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${
                    savingAttendance || loadingAttendance || clearingDay || clearingLesson || feriadoSelecionado
                      ? 'bg-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
                  }`}
                  title={feriadoSelecionado ? `Feriado nacional — ${feriadoSelecionado}` : undefined}
                >
                  {savingAttendance ? 'Salvando...' : 'Salvar Frequência'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmarTodosPresentesModal
        aberto={confirmandoTodosPresentes}
        totalAlunos={Object.keys(attendanceData).length}
        contexto={selectedLesson ? 'nesta aula' : 'neste dia'}
        salvando={savingAttendance}
        onConfirmar={() => handleSaveAttendance(true)}
        onRevisar={() => setConfirmandoTodosPresentes(false)}
      />
    </>
  );
}

export default Frequencia;
